import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  FiAlertTriangle,
  FiArrowUpRight,
  FiBarChart2,
  FiCheckCircle,
  FiMessageSquare,
  FiRefreshCw,
  FiTrendingUp,
} from "react-icons/fi";
import axios from "../api/axiosInstance";

const HISTORY_RANGE_OPTIONS = [
  { key: "1m", label: "Last month" },
  { key: "3m", label: "Last 3 months" },
  { key: "6m", label: "Last 6 months" },
  { key: "1y", label: "Last year" },
  { key: "2y", label: "Last 2 years" },
  { key: "5y", label: "Last 5 years" },
  { key: "all", label: "All" },
];

function toQueryString(params) {
  const search = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    search.append(key, String(value));
  });
  const query = search.toString();
  return query ? `?${query}` : "";
}

function formatNumber(value) {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric.toLocaleString("en-GB") : "--";
}

function formatScore(value) {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? `${Math.round(numeric)}/100` : "Pending";
}

function formatPercent(value) {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? `${Math.round(numeric)}%` : "--";
}

function formatDelta(value, suffix = "") {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "No comparison";
  const prefix = numeric > 0 ? "+" : "";
  return `${prefix}${Math.round(numeric)}${suffix}`;
}

function formatDate(value) {
  if (!value) return "No date";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
  }).format(parsed);
}

function buildExcerpt(value, maxLength = 150) {
  const normalized = String(value || "").trim().replace(/\s+/g, " ");
  if (!normalized) return "No detail available yet.";
  return normalized.length > maxLength ? `${normalized.slice(0, maxLength - 3)}...` : normalized;
}

function getSeverityTone(value) {
  switch (String(value || "").toLowerCase()) {
    case "danger":
      return "danger";
    case "warning":
      return "warning";
    case "success":
      return "success";
    default:
      return "neutral";
  }
}

function getPriorityTone(value) {
  switch (String(value || "").toLowerCase()) {
    case "high":
      return "danger";
    case "medium":
      return "warning";
    case "low":
      return "success";
    default:
      return "neutral";
  }
}

function buildToneClass(prefix, tone) {
  return `${prefix} ${prefix}--${tone}`;
}

function parseDateOnly(value) {
  if (!value) return null;
  const parsed = new Date(`${String(value).slice(0, 10)}T12:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function toDateOnlyString(value) {
  const parsed = value instanceof Date ? value : parseDateOnly(value);
  return parsed ? parsed.toISOString().slice(0, 10) : null;
}

function shiftDays(date, days) {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

function shiftYears(date, years) {
  const next = new Date(date);
  next.setFullYear(next.getFullYear() + years);
  return next;
}

function startOfWeek(date) {
  const next = new Date(date);
  const day = next.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  next.setDate(next.getDate() + diff);
  next.setHours(12, 0, 0, 0);
  return next;
}

function startOfMonth(date) {
  const next = new Date(date);
  next.setDate(1);
  next.setHours(12, 0, 0, 0);
  return next;
}

function startOfQuarter(date) {
  const next = new Date(date);
  next.setMonth(Math.floor(next.getMonth() / 3) * 3, 1);
  next.setHours(12, 0, 0, 0);
  return next;
}

function startOfYear(date) {
  const next = new Date(date);
  next.setMonth(0, 1);
  next.setHours(12, 0, 0, 0);
  return next;
}

function getRangeStartDate(rangeKey, endDate, firstDate) {
  switch (rangeKey) {
    case "1m":
      return shiftDays(endDate, -29);
    case "3m":
      return shiftDays(endDate, -89);
    case "6m":
      return shiftDays(endDate, -179);
    case "1y":
      return shiftDays(endDate, -364);
    case "2y":
      return shiftDays(endDate, -729);
    case "5y":
      return shiftDays(endDate, -1825);
    case "all":
    default:
      return firstDate || endDate;
  }
}

function getBucketMode(rangeKey, filteredDays) {
  if (rangeKey === "all") {
    return filteredDays > 3650 ? "year" : filteredDays > 730 ? "quarter" : "month";
  }
  if (rangeKey === "5y") return "quarter";
  if (rangeKey === "2y") return "month";
  if (rangeKey === "1y") return "month";
  if (rangeKey === "6m") return "week";
  return "day";
}

function getBucketDate(date, bucketMode) {
  switch (bucketMode) {
    case "week":
      return startOfWeek(date);
    case "month":
      return startOfMonth(date);
    case "quarter":
      return startOfQuarter(date);
    case "year":
      return startOfYear(date);
    case "day":
    default:
      return new Date(date);
  }
}

function formatHistoryBucketLabel(date, bucketMode) {
  if (!date) return "";
  if (bucketMode === "day") {
    return new Intl.DateTimeFormat("en-GB", { day: "2-digit", month: "short" }).format(date);
  }
  if (bucketMode === "week") {
    return new Intl.DateTimeFormat("en-GB", { day: "2-digit", month: "short" }).format(date);
  }
  if (bucketMode === "month") {
    return new Intl.DateTimeFormat("en-GB", { month: "short", year: "2-digit" }).format(date);
  }
  if (bucketMode === "quarter") {
    return new Intl.DateTimeFormat("en-GB", { month: "short", year: "2-digit" }).format(date);
  }
  return new Intl.DateTimeFormat("en-GB", { year: "numeric" }).format(date);
}

function formatRangeCaption(startDate, endDate) {
  if (!startDate || !endDate) return "No period selected";
  const formatter = new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
  return `${formatter.format(startDate)} to ${formatter.format(endDate)}`;
}

function buildHistoricalChartData(rawSeries, rangeKey) {
  const series = Array.isArray(rawSeries)
    ? rawSeries
        .map((row) => ({
          date: row?.date || null,
          dateValue: parseDateOnly(row?.date),
          reviews_count: Number(row?.reviews_count) || 0,
          avg_rating_100:
            row?.avg_rating_100 !== null && row?.avg_rating_100 !== undefined
              ? Number(row.avg_rating_100)
              : null,
        }))
        .filter((row) => row.dateValue)
        .sort((left, right) => left.dateValue - right.dateValue)
    : [];

  const firstDate = series[0]?.dateValue || null;
  const lastDate = new Date();
  lastDate.setHours(12, 0, 0, 0);
  const startDate = getRangeStartDate(rangeKey, lastDate, firstDate);

  const filteredRows = series.filter((row) => row.dateValue >= startDate && row.dateValue <= lastDate);
  const filteredDays = Math.max(
    1,
    Math.round((lastDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1
  );
  const bucketMode = getBucketMode(rangeKey, filteredDays);
  const buckets = new Map();

  filteredRows.forEach((row) => {
    const bucketDate = getBucketDate(row.dateValue, bucketMode);
    const bucketKey = toDateOnlyString(bucketDate);
    if (!buckets.has(bucketKey)) {
      buckets.set(bucketKey, {
        date: bucketKey,
        dateValue: bucketDate,
        reviews_count: 0,
        rating_total: 0,
        rating_weight: 0,
      });
    }

    const bucket = buckets.get(bucketKey);
    bucket.reviews_count += row.reviews_count;
    if (Number.isFinite(row.avg_rating_100) && row.reviews_count > 0) {
      bucket.rating_total += row.avg_rating_100 * row.reviews_count;
      bucket.rating_weight += row.reviews_count;
    }
  });

  const bucketedSeries = Array.from(buckets.values())
    .sort((left, right) => left.dateValue - right.dateValue)
    .map((bucket) => ({
      date: bucket.date,
      label: formatHistoryBucketLabel(bucket.dateValue, bucketMode),
      reviews_count: bucket.reviews_count,
      avg_rating_100:
        bucket.rating_weight > 0 ? Number((bucket.rating_total / bucket.rating_weight).toFixed(2)) : null,
    }));

  const totalReviews = filteredRows.reduce((sum, row) => sum + row.reviews_count, 0);
  const weightedRatingTotal = filteredRows.reduce((sum, row) => {
    if (!Number.isFinite(row.avg_rating_100) || row.reviews_count <= 0) return sum;
    return sum + row.avg_rating_100 * row.reviews_count;
  }, 0);
  const averageRating = totalReviews > 0 ? Number((weightedRatingTotal / totalReviews).toFixed(2)) : null;

  return {
    series: bucketedSeries,
    totalReviews,
    averageRating,
    bucketMode,
    startDate,
    endDate: lastDate,
    caption: formatRangeCaption(startDate, lastDate),
  };
}

function ReviewsDashboardPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const businessUnitId = searchParams.get("business_unit_id") || "";
  const [businessUnit, setBusinessUnit] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [feedback, setFeedback] = useState("");
  const [historyRange, setHistoryRange] = useState("1y");

  const fetchDashboard = async ({ silent = false } = {}) => {
    silent ? setRefreshing(true) : setLoading(true);
    setError("");

    try {
      const query = toQueryString({ business_unit_id: businessUnitId || undefined });
      const response = await axios.get(`/api/reviews-intelligence/dashboard${query}`);
      setBusinessUnit(response.data?.business_unit || null);
      setDashboard(response.data?.dashboard || null);
    } catch (requestError) {
      setError(requestError?.response?.data?.error || "Error loading reviews dashboard.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    setFeedback("");
    fetchDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessUnitId]);

  const handleRefreshDerivedData = async () => {
    setRefreshing(true);
    setFeedback("");
    setError("");

    try {
      const response = await axios.post("/api/reviews-intelligence/dashboard/refresh-data", {
        business_unit_id: businessUnitId,
      });
      setBusinessUnit(response.data?.business_unit || null);
      setDashboard(response.data?.dashboard || null);
      setFeedback(response.data?.message || "Dashboard data refreshed.");
    } catch (requestError) {
      setError(requestError?.response?.data?.error || "Error refreshing dashboard data.");
    } finally {
      setRefreshing(false);
    }
  };

  const topicEvolution = dashboard?.topic_evolution || [];
  const maxEvolutionCount = useMemo(() => {
    const counts = topicEvolution.flatMap((entry) =>
      (entry.series || []).map((point) => Math.max(point.positive_reviews_count || 0, point.negative_reviews_count || 0))
    );
    return Math.max(1, ...counts);
  }, [topicEvolution]);

  const historicalChart = useMemo(
    () => buildHistoricalChartData(dashboard?.historical_rating?.series || [], historyRange),
    [dashboard?.historical_rating?.series, historyRange]
  );

  const chartGeometry = useMemo(() => {
    const points = historicalChart.series.filter((point) => Number.isFinite(point.avg_rating_100));
    if (!points.length) {
      return {
        width: 620,
        height: 188,
        paddingLeft: 20,
        paddingTop: 12,
        innerHeight: 138,
        plotBottom: 150,
        areaPath: "",
        linePath: "",
        points: [],
        labels: [],
        reviewBars: [],
      };
    }

    const width = 620;
    const height = 188;
    const paddingLeft = 20;
    const paddingRight = 14;
    const paddingTop = 12;
    const paddingBottom = 38;
    const innerWidth = Math.max(1, width - paddingLeft - paddingRight);
    const innerHeight = Math.max(1, height - paddingTop - paddingBottom);
    const pointCount = points.length;
    const maxReviewsCount = Math.max(1, ...points.map((point) => Number(point.reviews_count) || 0));
    const barBaseY = paddingTop + innerHeight;
    const barMaxHeight = 18;
    const barWidth = Math.max(3, Math.min(10, (innerWidth / Math.max(pointCount, 1)) * 0.36));

    const chartPoints = points.map((point, index) => {
      const x =
        pointCount === 1
          ? paddingLeft + innerWidth / 2
          : paddingLeft + (innerWidth * index) / (pointCount - 1);
      const y = paddingTop + innerHeight - (innerHeight * (point.avg_rating_100 || 0)) / 100;
      return {
        ...point,
        x,
        y,
      };
    });

    const reviewBars = chartPoints.map((point) => {
      const reviewsCount = Number(point.reviews_count) || 0;
      const heightValue = Math.max(3, (reviewsCount / maxReviewsCount) * barMaxHeight);
      return {
        key: `${point.date}-reviews`,
        x: point.x - barWidth / 2,
        y: barBaseY - heightValue,
        width: barWidth,
        height: heightValue,
        reviews_count: reviewsCount,
        label: point.label,
      };
    });

    const linePath = chartPoints.reduce(
      (path, point, index) => `${path}${index === 0 ? "M" : " L"} ${point.x.toFixed(2)} ${point.y.toFixed(2)}`,
      ""
    );
    const areaPath = `${linePath} L ${chartPoints[chartPoints.length - 1].x.toFixed(2)} ${(paddingTop + innerHeight).toFixed(2)} L ${chartPoints[0].x.toFixed(2)} ${(paddingTop + innerHeight).toFixed(2)} Z`;

    const labelIndexes = Array.from(
      new Set(
        chartPoints.map((_, index) =>
          pointCount <= 5 ? index : Math.round((index * (pointCount - 1)) / 4)
        )
      )
    );

    return {
      width,
      height,
      paddingLeft,
      paddingTop,
      innerHeight,
      barBaseY,
      plotBottom: paddingTop + innerHeight,
      areaPath,
      linePath,
      points: chartPoints,
      reviewBars,
      labels: labelIndexes.map((index) => chartPoints[index]).filter(Boolean),
    };
  }, [historicalChart.series]);

  const uiCss = `
    .reservations-container.rid-pageShell{padding:10px}
    .reservations-container .rid-shell{background:linear-gradient(180deg,rgba(25,27,53,.94),rgba(11,12,25,.97));border:1px solid rgba(255,255,255,.08);border-radius:22px;box-shadow:0 24px 64px rgba(3,7,18,.32);overflow:hidden}
    .reservations-container .rid-header{display:flex;justify-content:space-between;align-items:flex-end;gap:18px;padding:18px 18px 14px;border-bottom:1px solid rgba(255,255,255,.08);flex-wrap:wrap}
    .reservations-container .rid-title{margin:0;font-size:1.42rem;font-weight:800;letter-spacing:-.02em;color:rgba(244,248,255,.98)}
    .reservations-container .rid-subtitle{margin:6px 0 0;font-size:.82rem;line-height:1.55;color:rgba(214,226,247,.68);max-width:860px}
    .reservations-container .rid-hotel{margin-top:8px;font-size:.76rem;color:rgba(214,226,247,.62)}
    .reservations-container .rid-actions{display:flex;gap:8px;flex-wrap:wrap}
    .reservations-container .rid-toolbarBtn{border:1px solid rgba(255,255,255,.14);background:rgba(255,255,255,.04);color:rgba(244,248,255,.92);border-radius:10px;padding:8px 12px;font-size:.78rem;font-weight:700;display:inline-flex;align-items:center;gap:7px}
    .reservations-container .rid-toolbarBtn:disabled{opacity:.45;cursor:not-allowed}
    .reservations-container .rid-notice{margin:14px 18px 0;padding:12px 14px;border-radius:14px;font-size:.78rem;line-height:1.55;border:1px solid rgba(255,255,255,.08)}
    .reservations-container .rid-notice--success{background:rgba(25,164,122,.16);color:rgba(176,246,220,.98);border-color:rgba(25,164,122,.32)}
    .reservations-container .rid-notice--error{background:rgba(177,48,69,.22);color:rgba(255,213,213,.98);border-color:rgba(255,125,147,.32)}
    .reservations-container .rid-body{display:flex;flex-direction:column;gap:16px;padding:14px 18px 18px}
    .reservations-container .rid-kpis{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:12px}
    .reservations-container .rid-kpiCard,.rid-panel,.rid-miniPanel{background:rgba(255,255,255,.04);border:1px solid rgba(255,255,255,.08);border-radius:18px}
    .reservations-container .rid-kpiCard{padding:16px}
    .reservations-container .rid-kpiLabel,.rid-panelLabel{font-size:.68rem;font-weight:800;letter-spacing:.08em;text-transform:uppercase;color:rgba(214,226,247,.62)}
    .reservations-container .rid-kpiValue{margin-top:10px;font-size:1.18rem;font-weight:850;color:rgba(244,248,255,.97)}
    .reservations-container .rid-kpiMeta{margin-top:7px;font-size:.8rem;line-height:1.5;color:rgba(214,226,247,.68)}
    .reservations-container .rid-grid{display:grid;grid-template-columns:1.05fr .95fr;gap:16px}
    .reservations-container .rid-panel{padding:16px}
    .reservations-container .rid-panelHeader{display:flex;justify-content:space-between;align-items:flex-start;gap:12px;margin-bottom:12px}
    .reservations-container .rid-panelTitle{margin:0;font-size:1rem;font-weight:800;color:rgba(244,248,255,.97)}
    .reservations-container .rid-panelText{margin:4px 0 0;font-size:.8rem;line-height:1.55;color:rgba(214,226,247,.68)}
    .reservations-container .rid-grid--history{grid-template-columns:minmax(0,1.2fr) minmax(240px,.8fr);align-items:start}
    .reservations-container .rid-panel--history{padding:13px 13px 11px}
    .reservations-container .rid-panel--historySide{padding:12px;align-self:start}
    .reservations-container .rid-historyHeader{display:flex;justify-content:space-between;align-items:flex-start;gap:12px;flex-wrap:wrap;margin-bottom:10px}
    .reservations-container .rid-rangeGroup{display:flex;gap:8px;flex-wrap:wrap}
    .reservations-container .rid-rangeBtn{border:1px solid rgba(255,255,255,.14);background:rgba(255,255,255,.04);color:rgba(244,248,255,.88);border-radius:999px;padding:5px 9px;font-size:.69rem;font-weight:700}
    .reservations-container .rid-rangeBtn--active{background:linear-gradient(135deg,rgba(48,216,255,.18),rgba(139,92,246,.26));border-color:rgba(109,184,255,.34);color:rgba(247,250,255,.98)}
    .reservations-container .rid-historyStats{display:grid;grid-template-columns:1fr;gap:8px}
    .reservations-container .rid-historyStat{padding:10px 12px;border-radius:14px;border:1px solid rgba(255,255,255,.06);background:rgba(255,255,255,.03)}
    .reservations-container .rid-historyStatLabel{font-size:.67rem;letter-spacing:.08em;text-transform:uppercase;color:rgba(214,226,247,.58);font-weight:800}
    .reservations-container .rid-historyStatValue{margin-top:5px;font-size:.92rem;font-weight:800;color:rgba(244,248,255,.96)}
    .reservations-container .rid-historyMeta{display:flex;flex-direction:column;gap:8px}
    .reservations-container .rid-historyBlurb{padding:10px 12px;border-radius:14px;border:1px solid rgba(255,255,255,.06);background:rgba(255,255,255,.03);font-size:.72rem;line-height:1.5;color:rgba(214,226,247,.66)}
    .reservations-container .rid-chartWrap{padding:6px 8px 8px;border-radius:14px;border:1px solid rgba(255,255,255,.08);background:rgba(7,10,20,.28)}
    .reservations-container .rid-chartSvg{width:100%;height:auto;display:block;overflow:visible}
    .reservations-container .rid-chartGrid{stroke:rgba(255,255,255,.08);stroke-width:1}
    .reservations-container .rid-chartAxisLabel{font-size:9px;fill:rgba(214,226,247,.48)}
    .reservations-container .rid-chartAxisSubLabel{font-size:8px;fill:rgba(214,226,247,.34)}
    .reservations-container .rid-chartArea{fill:url(#ridHistoryArea);opacity:.42}
    .reservations-container .rid-chartLine{fill:none;stroke:#5ad9ff;stroke-width:2;stroke-linecap:round;stroke-linejoin:round}
    .reservations-container .rid-chartPoint{fill:#eef8ff;stroke:#5ad9ff;stroke-width:1.5}
    .reservations-container .rid-chartBar{fill:rgba(244,248,255,.1)}
    .reservations-container .rid-chartLegend{display:flex;justify-content:space-between;gap:12px;flex-wrap:wrap;margin-top:8px}
    .reservations-container .rid-chartLegendText{font-size:.67rem;line-height:1.45;color:rgba(214,226,247,.56)}
    .reservations-container .rid-alertList,.reservations-container .rid-topicList,.reservations-container .rid-miniList{display:flex;flex-direction:column;gap:10px}
    .reservations-container .rid-alertItem,.reservations-container .rid-topicItem,.reservations-container .rid-miniItem{padding:12px 14px;border-radius:14px;border:1px solid rgba(255,255,255,.08);background:rgba(255,255,255,.03)}
    .reservations-container .rid-alertItem{display:flex;gap:12px;align-items:flex-start}
    .reservations-container .rid-alertIcon{width:32px;height:32px;border-radius:10px;display:inline-flex;align-items:center;justify-content:center;flex:0 0 auto;background:rgba(255,255,255,.06);color:rgba(244,248,255,.95)}
    .reservations-container .rid-topicHeader,.reservations-container .rid-miniHeader{display:flex;justify-content:space-between;align-items:flex-start;gap:10px}
    .reservations-container .rid-topicTitle,.reservations-container .rid-miniTitle{font-size:.84rem;font-weight:700;color:rgba(245,248,255,.97)}
    .reservations-container .rid-topicMeta,.reservations-container .rid-miniMeta,.reservations-container .rid-smallText{margin-top:4px;font-size:.74rem;line-height:1.55;color:rgba(214,226,247,.64)}
    .reservations-container .rid-chip{display:inline-flex;align-items:center;justify-content:center;border-radius:999px;padding:5px 10px;font-size:.68rem;font-weight:800;white-space:nowrap}
    .reservations-container .rid-chip--neutral{background:rgba(255,255,255,.08);color:rgba(212,224,244,.95)}
    .reservations-container .rid-chip--success{background:rgba(25,164,122,.25);color:rgba(176,246,220,.98)}
    .reservations-container .rid-chip--warning{background:rgba(255,193,95,.14);color:rgba(255,230,185,.98)}
    .reservations-container .rid-chip--danger{background:rgba(177,48,69,.24);color:rgba(255,204,204,.98)}
    .reservations-container .rid-evolution{display:flex;flex-direction:column;gap:12px}
    .reservations-container .rid-evolutionRow{display:grid;grid-template-columns:140px 1fr;gap:12px;align-items:center}
    .reservations-container .rid-bars{display:flex;gap:4px;align-items:flex-end;min-height:68px}
    .reservations-container .rid-bar{flex:1;border-radius:999px 999px 6px 6px;min-height:6px}
    .reservations-container .rid-bar--positive{background:linear-gradient(180deg,rgba(25,164,122,.92),rgba(11,110,81,.85))}
    .reservations-container .rid-bar--negative{background:linear-gradient(180deg,rgba(177,48,69,.95),rgba(122,28,47,.88))}
    .reservations-container .rid-tableWrap{border:1px solid rgba(255,255,255,.08);border-radius:18px;overflow:auto;background:rgba(7,10,20,.36)}
    .reservations-container .rid-table{width:100%;min-width:980px;border-collapse:separate;border-spacing:0;table-layout:fixed}
    .reservations-container .rid-table th,.reservations-container .rid-table td{padding:12px 16px;text-align:left;white-space:normal;overflow-wrap:anywhere;word-break:break-word}
    .reservations-container .rid-table th{font-size:.68rem;letter-spacing:.08em;text-transform:uppercase;color:rgba(214,226,247,.52);border-bottom:1px solid rgba(255,255,255,.08);background:rgba(255,255,255,.03)}
    .reservations-container .rid-table td{font-size:.8rem;vertical-align:top;color:rgba(230,236,247,.88);border-bottom:1px solid rgba(255,255,255,.06)}
    .reservations-container .rid-table tr:hover td{background:rgba(255,255,255,.02)}
    .reservations-container .rid-actionBtn{border:1px solid rgba(255,255,255,.14);background:rgba(255,255,255,.04);color:rgba(244,248,255,.92);border-radius:10px;padding:7px 10px;font-size:.76rem;font-weight:700;display:inline-flex;align-items:center;gap:6px}
    .reservations-container .rid-empty{padding:28px;text-align:center;font-size:.82rem;line-height:1.7;color:rgba(214,226,247,.72)}
    @media (max-width:1180px){.reservations-container .rid-kpis{grid-template-columns:repeat(2,minmax(0,1fr))}.reservations-container .rid-grid,.reservations-container .rid-grid--history{grid-template-columns:1fr}}
    @media (max-width:860px){.reservations-container.rid-pageShell{padding:8px}.reservations-container .rid-header,.reservations-container .rid-actions,.reservations-container .rid-panelHeader,.reservations-container .rid-historyHeader{flex-direction:column;align-items:stretch}.reservations-container .rid-kpis{grid-template-columns:1fr}.reservations-container .rid-evolutionRow{grid-template-columns:1fr}.reservations-container .rid-bars{min-height:54px}}
  `;

  const goToOperations = () =>
    navigate(`/reviews-intelligence/operations?business_unit_id=${encodeURIComponent(String(businessUnitId))}`);
  const goToActionPlans = () =>
    navigate(`/reviews-intelligence/trends-actions?business_unit_id=${encodeURIComponent(String(businessUnitId))}`);

  return (
    <div className="reservations-container rid-pageShell">
      <style>{uiCss}</style>
      <section className="rid-shell">
        <div className="rid-header">
          <div>
            <h1 className="rid-title">Reviews Dashboard</h1>
            <p className="rid-subtitle">
              Executive snapshot of hotel reputation, guest pain points, reply workload and operational actions, all connected to the review data already saved in the PMS.
            </p>
            {businessUnit?.name ? <div className="rid-hotel">Current hotel: <strong>{businessUnit.name}</strong></div> : null}
          </div>
          <div className="rid-actions">
            <button type="button" className="rid-toolbarBtn" onClick={handleRefreshDerivedData} disabled={refreshing}>
              <FiTrendingUp />
              {refreshing ? "Refreshing..." : "Refresh dashboard data"}
            </button>
            <button type="button" className="rid-toolbarBtn" onClick={() => fetchDashboard({ silent: true })} disabled={refreshing || loading}>
              <FiRefreshCw />
              {refreshing ? "Refreshing..." : "Refresh view"}
            </button>
          </div>
        </div>
        {feedback ? <div className="rid-notice rid-notice--success">{feedback}</div> : null}
        {error ? <div className="rid-notice rid-notice--error">{error}</div> : null}
        <div className="rid-body">
          {loading ? (
            <div className="rid-empty">Loading reviews dashboard...</div>
          ) : (
            <>
              <div className="rid-kpis">
                <article className="rid-kpiCard"><div className="rid-kpiLabel">Average rating 30d</div><div className="rid-kpiValue">{formatScore(dashboard?.kpis?.average_rating_30)}</div><div className="rid-kpiMeta">vs previous 30 days: {formatDelta(dashboard?.kpis?.rating_trend_vs_previous_30, " pts")}</div></article>
                <article className="rid-kpiCard"><div className="rid-kpiLabel">Review volume</div><div className="rid-kpiValue">{formatNumber(dashboard?.kpis?.review_volume_30)}</div><div className="rid-kpiMeta">90 days: {formatNumber(dashboard?.kpis?.review_volume_90)} · change: {formatDelta(dashboard?.kpis?.volume_trend_vs_previous_30, "%")}</div></article>
                <article className="rid-kpiCard"><div className="rid-kpiLabel">Sentiment split 30d</div><div className="rid-kpiValue">{formatPercent(dashboard?.kpis?.positive_percentage_30)} / {formatPercent(dashboard?.kpis?.negative_percentage_30)}</div><div className="rid-kpiMeta">Positive vs negative share in the latest month</div></article>
                <article className="rid-kpiCard"><div className="rid-kpiLabel">Reply workload</div><div className="rid-kpiValue">{formatNumber(dashboard?.kpis?.pending_replies)}</div><div className="rid-kpiMeta">Recommended replies: {formatNumber(dashboard?.kpis?.recommended_replies)} · Critical reviews: {formatNumber(dashboard?.kpis?.critical_reviews_30)}</div></article>
              </div>

              <div className="rid-grid rid-grid--history">
                <section className="rid-panel rid-panel--history">
                  <div className="rid-historyHeader">
                    <div>
                      <h2 className="rid-panelTitle">Hotel rating evolution</h2>
                      <p className="rid-panelText">Average rating over time, based on the reviews already saved in the PMS.</p>
                    </div>
                    <div className="rid-rangeGroup">
                      {HISTORY_RANGE_OPTIONS.map((option) => (
                        <button
                          key={option.key}
                          type="button"
                          className={`rid-rangeBtn ${historyRange === option.key ? "rid-rangeBtn--active" : ""}`}
                          onClick={() => setHistoryRange(option.key)}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="rid-chartWrap">
                    {chartGeometry.points.length ? (
                      <>
                        <svg
                          className="rid-chartSvg"
                          viewBox={`0 0 ${chartGeometry.width} ${chartGeometry.height}`}
                          role="img"
                          aria-label="Hotel rating evolution chart"
                        >
                          <defs>
                            <linearGradient id="ridHistoryArea" x1="0" x2="0" y1="0" y2="1">
                              <stop offset="0%" stopColor="rgba(90,217,255,0.28)" />
                              <stop offset="100%" stopColor="rgba(90,217,255,0.02)" />
                            </linearGradient>
                          </defs>
                          {[0, 25, 50, 75, 100].map((tick) => {
                            const y = chartGeometry.paddingTop + (chartGeometry.innerHeight * (100 - tick)) / 100;
                            return (
                              <g key={tick}>
                                <line
                                  x1={chartGeometry.paddingLeft}
                                  x2={chartGeometry.width - 12}
                                  y1={y}
                                  y2={y}
                                  className="rid-chartGrid"
                                />
                                <text x="10" y={y + 4} textAnchor="end" className="rid-chartAxisLabel">
                                  {tick}
                                </text>
                              </g>
                            );
                          })}
                          {chartGeometry.reviewBars.map((bar) => (
                            <rect
                              key={bar.key}
                              x={bar.x}
                              y={bar.y}
                              width={bar.width}
                              height={bar.height}
                              rx="5"
                              className="rid-chartBar"
                            >
                              <title>{`${bar.label}: ${formatNumber(bar.reviews_count)} reviews`}</title>
                            </rect>
                          ))}
                          <path d={chartGeometry.areaPath} className="rid-chartArea" />
                          <path d={chartGeometry.linePath} className="rid-chartLine" />
                          {chartGeometry.points.map((point) => (
                            <circle key={`${point.date}-${point.label}`} cx={point.x} cy={point.y} r="3.2" className="rid-chartPoint">
                              <title>{`${point.label}: ${formatScore(point.avg_rating_100)} from ${formatNumber(point.reviews_count)} reviews`}</title>
                            </circle>
                          ))}
                          {chartGeometry.labels.map((point) => (
                            <g key={`label-${point.date}`}>
                              <text
                                x={point.x}
                                y={chartGeometry.height - 18}
                                textAnchor="middle"
                                className="rid-chartAxisLabel"
                              >
                                {point.label}
                              </text>
                              <text
                                x={point.x}
                                y={chartGeometry.height - 6}
                                textAnchor="middle"
                                className="rid-chartAxisSubLabel"
                              >
                                {formatNumber(point.reviews_count)}
                              </text>
                            </g>
                          ))}
                        </svg>
                        <div className="rid-chartLegend">
                          <div className="rid-chartLegendText">
                            {formatNumber(dashboard?.historical_rating?.total_reviews)} total reviews on record. Numbers below each date show review count.
                          </div>
                          <div className="rid-chartLegendText">
                            History from {dashboard?.historical_rating?.first_review_date || "unknown"} to {dashboard?.historical_rating?.last_review_date || "unknown"}.
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="rid-empty">There are not enough reviews in the selected period to draw the evolution chart yet.</div>
                    )}
                  </div>
                </section>

                <section className="rid-panel rid-panel--historySide">
                  <div className="rid-historyMeta">
                    <div>
                      <h2 className="rid-panelTitle">Period summary</h2>
                      <p className="rid-panelText">Quick context for the chart range.</p>
                    </div>
                    <div className="rid-historyStats">
                      <article className="rid-historyStat">
                        <div className="rid-historyStatLabel">Reviews in selected period</div>
                        <div className="rid-historyStatValue">{formatNumber(historicalChart.totalReviews)}</div>
                      </article>
                      <article className="rid-historyStat">
                        <div className="rid-historyStatLabel">Average score in period</div>
                        <div className="rid-historyStatValue">{formatScore(historicalChart.averageRating)}</div>
                      </article>
                      <article className="rid-historyStat">
                        <div className="rid-historyStatLabel">Period shown</div>
                        <div className="rid-historyStatValue">{historicalChart.caption}</div>
                      </article>
                    </div>
                    <div className="rid-historyBlurb">
                      Use the range selector to zoom from recent reputation to the full historical view without leaving the dashboard.
                    </div>
                  </div>
                </section>
              </div>

              <div className="rid-grid">
                <section className="rid-panel">
                  <div className="rid-panelHeader">
                    <div>
                      <h2 className="rid-panelTitle">Alerts</h2>
                      <p className="rid-panelText">Simple reputation warnings generated from the latest guest patterns.</p>
                    </div>
                  </div>
                  {dashboard?.alerts?.length ? (
                    <div className="rid-alertList">
                      {dashboard.alerts.map((alert) => (
                        <article key={alert.key} className="rid-alertItem">
                          <span className="rid-alertIcon">
                            <FiAlertTriangle />
                          </span>
                          <div>
                            <div className="rid-topicTitle">{alert.title}</div>
                            <div className="rid-topicMeta">{alert.message}</div>
                          </div>
                        </article>
                      ))}
                    </div>
                  ) : (
                    <div className="rid-empty">No alerts right now. The recent review flow looks stable.</div>
                  )}
                </section>

                <section className="rid-panel">
                  <div className="rid-panelHeader">
                    <div>
                      <h2 className="rid-panelTitle">Top strengths</h2>
                      <p className="rid-panelText">What guests are praising most often in recent reviews.</p>
                    </div>
                  </div>
                  {dashboard?.top_strengths?.length ? (
                    <div className="rid-topicList">
                      {dashboard.top_strengths.map((item) => (
                        <article key={item.topic_id} className="rid-topicItem">
                          <div className="rid-topicHeader">
                            <div className="rid-topicTitle">{item.topic?.label || item.topic?.code || "Topic"}</div>
                            <span className={buildToneClass("rid-chip", "success")}>{formatNumber(item.positive_reviews_count)} mentions</span>
                          </div>
                          <div className="rid-topicMeta">
                            Average score {formatScore(item.avg_rating_100)} · Group {item.topic?.topic_group || "general"}
                          </div>
                        </article>
                      ))}
                    </div>
                  ) : (
                    <div className="rid-empty">No clear strengths have been detected yet.</div>
                  )}
                </section>
              </div>

              <div className="rid-grid">
                <section className="rid-panel">
                  <div className="rid-panelHeader">
                    <div>
                      <h2 className="rid-panelTitle">Top weaknesses</h2>
                      <p className="rid-panelText">The issues that appear most often or need faster operational follow-up.</p>
                    </div>
                    <button type="button" className="rid-actionBtn" onClick={goToActionPlans}>
                      <FiArrowUpRight />
                      Open action plans
                    </button>
                  </div>
                  {dashboard?.top_weaknesses?.length ? (
                    <div className="rid-topicList">
                      {dashboard.top_weaknesses.map((item) => (
                        <article key={item.topic_id} className="rid-topicItem">
                          <div className="rid-topicHeader">
                            <div className="rid-topicTitle">{item.topic?.label || item.topic?.code || "Topic"}</div>
                            <span className={buildToneClass("rid-chip", getPriorityTone(item.priority))}>
                              {formatScore(item.urgency_score)}
                            </span>
                          </div>
                          <div className="rid-topicMeta">
                            Negative mentions {formatNumber(item.negative_reviews_count)} · Trend {formatDelta(item.trend_delta_percent, "%")} · Impact {formatScore(item.impact_score)}
                          </div>
                          <div className="rid-smallText">
                            Current plan: {item.status ? String(item.status).replace(/_/g, " ") : "not created yet"}
                          </div>
                        </article>
                      ))}
                    </div>
                  ) : (
                    <div className="rid-empty">No repeated weaknesses have been detected yet.</div>
                  )}
                </section>

                <section className="rid-panel">
                  <div className="rid-panelHeader">
                    <div>
                      <h2 className="rid-panelTitle">Topic evolution</h2>
                      <p className="rid-panelText">Daily positive and negative mentions for the most visible topics in the last 30 days.</p>
                    </div>
                  </div>
                  {topicEvolution.length ? (
                    <div className="rid-evolution">
                      {topicEvolution.map((entry) => (
                        <div key={entry.topic?.id || entry.topic?.code} className="rid-evolutionRow">
                          <div>
                            <div className="rid-topicTitle">{entry.topic?.label || entry.topic?.code || "Topic"}</div>
                            <div className="rid-topicMeta">{entry.topic?.topic_group || "general"}</div>
                          </div>
                          <div className="rid-bars">
                            {(entry.series || []).map((point) => {
                              const negativeHeight = Math.max(6, ((point.negative_reviews_count || 0) / maxEvolutionCount) * 68);
                              const positiveHeight = Math.max(6, ((point.positive_reviews_count || 0) / maxEvolutionCount) * 68);
                              const hasNegative = (point.negative_reviews_count || 0) >= (point.positive_reviews_count || 0);
                              return (
                                <div
                                  key={`${entry.topic?.id || entry.topic?.code}-${point.date}`}
                                  className={`rid-bar ${hasNegative ? "rid-bar--negative" : "rid-bar--positive"}`}
                                  style={{ height: `${hasNegative ? negativeHeight : positiveHeight}px` }}
                                  title={`${formatDate(point.date)} · +${point.positive_reviews_count || 0} / -${point.negative_reviews_count || 0}`}
                                />
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="rid-empty">Not enough topic history yet to plot the evolution.</div>
                  )}
                </section>
              </div>

              <div className="rid-grid">
                <section className="rid-panel">
                  <div className="rid-panelHeader">
                    <div>
                      <h2 className="rid-panelTitle">Needs reply now</h2>
                      <p className="rid-panelText">The reviews that should be checked first by the team.</p>
                    </div>
                    <button type="button" className="rid-actionBtn" onClick={goToOperations}>
                      <FiMessageSquare />
                      Open operations
                    </button>
                  </div>
                  <div className="rid-tableWrap">
                    {dashboard?.pending_reply_reviews?.length ? (
                      <table className="rid-table">
                        <colgroup>
                          <col style={{ width: "110px" }} />
                          <col style={{ width: "130px" }} />
                          <col />
                          <col style={{ width: "120px" }} />
                          <col style={{ width: "120px" }} />
                        </colgroup>
                        <thead>
                          <tr>
                            <th>Date</th>
                            <th>Channel</th>
                            <th>Guest review</th>
                            <th>Criticality</th>
                            <th>Draft</th>
                          </tr>
                        </thead>
                        <tbody>
                          {dashboard.pending_reply_reviews.map((review) => (
                            <tr key={review.id}>
                              <td><div className="rid-topicTitle">{formatDate(review.review_date || review.created_at)}</div><div className="rid-topicMeta">{formatNumber(review.rating_value)}/{formatNumber(review.rating_scale_max)}</div></td>
                              <td><div className="rid-topicTitle">{review?.source?.name || "Unknown channel"}</div><div className="rid-topicMeta">{review.ai_ready ? "AI ready" : "AI pending"}</div></td>
                              <td><div className="rid-topicTitle">{review.guest_display_name || review.guest_name || "Anonymous guest"}</div><div className="rid-topicMeta">{buildExcerpt(review.title || review.review_text)}</div></td>
                              <td><span className={buildToneClass("rid-chip", "danger")}>{formatScore(review.critical_points_score)}</span></td>
                              <td><span className={buildToneClass("rid-chip", review.draft_ready ? "success" : "neutral")}>{review.draft_ready ? "Draft ready" : "No draft"}</span></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <div className="rid-empty">There are no reply tasks waiting right now.</div>
                    )}
                  </div>
                </section>

                <section className="rid-panel">
                  <div className="rid-panelHeader">
                    <div>
                      <h2 className="rid-panelTitle">Open action plans</h2>
                      <p className="rid-panelText">The main operational themes currently being tracked.</p>
                    </div>
                  </div>
                  {dashboard?.action_plan_summary?.length ? (
                    <div className="rid-miniList">
                      {dashboard.action_plan_summary.map((plan) => (
                        <article key={plan.id} className="rid-miniItem">
                          <div className="rid-miniHeader">
                            <div className="rid-miniTitle">{plan.topic?.label || plan.topic?.code || "Topic"}</div>
                            <span className={buildToneClass("rid-chip", getPriorityTone(plan.priority))}>
                              {plan.priority || "review"}
                            </span>
                          </div>
                          <div className="rid-miniMeta">
                            Owner: {plan.owner_user?.name || "Unassigned"} · Urgency {formatScore(plan.urgency_score)}
                          </div>
                          <div className="rid-smallText">
                            {buildExcerpt(plan.final_recommendation || plan.ai_recommendation || plan.current_problem_summary)}
                          </div>
                        </article>
                      ))}
                    </div>
                  ) : (
                    <div className="rid-empty">No open action plans yet.</div>
                  )}
                </section>
              </div>

              <div className="rid-grid">
                <section className="rid-panel">
                  <div className="rid-panelHeader">
                    <div>
                      <h2 className="rid-panelTitle">Source comparison</h2>
                      <p className="rid-panelText">How each review channel is performing in the recent period.</p>
                    </div>
                  </div>
                  {dashboard?.source_comparison?.length ? (
                    <div className="rid-miniList">
                      {dashboard.source_comparison.map((entry) => (
                        <article key={entry.source?.id || entry.source?.code} className="rid-miniItem">
                          <div className="rid-miniHeader">
                            <div className="rid-miniTitle">{entry.source?.name || "Unknown channel"}</div>
                            <span className={buildToneClass("rid-chip", "neutral")}>{formatNumber(entry.reviews_count)} reviews</span>
                          </div>
                          <div className="rid-miniMeta">
                            Average score {formatScore(entry.avg_rating_100)} · Negative {formatNumber(entry.negative_reviews_count)} · Positive {formatNumber(entry.positive_reviews_count)}
                          </div>
                        </article>
                      ))}
                    </div>
                  ) : (
                    <div className="rid-empty">Not enough source data yet to compare channels.</div>
                  )}
                </section>

                <section className="rid-panel">
                  <div className="rid-panelHeader">
                    <div>
                      <h2 className="rid-panelTitle">Internal feedback signal</h2>
                      <p className="rid-panelText">Direct guest survey score tracked alongside public reputation.</p>
                    </div>
                  </div>
                  <div className="rid-miniList">
                    <article className="rid-miniItem">
                      <div className="rid-miniHeader">
                        <div className="rid-miniTitle">Internal survey average</div>
                        <span className={buildToneClass("rid-chip", "success")}>{formatScore(dashboard?.kpis?.internal_survey_score_90)}</span>
                      </div>
                      <div className="rid-miniMeta">
                        Based on internal feedback recorded in the last 90 days.
                      </div>
                    </article>
                    <article className="rid-miniItem">
                      <div className="rid-miniHeader">
                        <div className="rid-miniTitle">Latest aggregate date</div>
                        <span className={buildToneClass("rid-chip", "neutral")}>{dashboard?.freshness?.latest_aggregate_date || "Not built"}</span>
                      </div>
                      <div className="rid-miniMeta">
                        Loaded aggregate rows: {formatNumber(dashboard?.freshness?.aggregates_rows_loaded)}
                      </div>
                    </article>
                    <article className="rid-miniItem">
                      <div className="rid-miniHeader">
                        <div className="rid-miniTitle">Operational readiness</div>
                        <span className={buildToneClass("rid-chip", dashboard?.alerts?.length ? "warning" : "success")}>
                          {dashboard?.alerts?.length ? "Needs attention" : "Stable"}
                        </span>
                      </div>
                      <div className="rid-miniMeta">
                        This flag turns cautious when the dashboard detects spikes, critical topics or reply backlog.
                      </div>
                    </article>
                  </div>
                </section>
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
}

export default ReviewsDashboardPage;
